/* @flow */

import * as React from 'react';

import type {EventForTimeline} from '../types';

import {getQuoteLinkText, getTargetEntities, makeTargetEntitiesDisplay} from './helpers';
import {TimelineBanner} from './timeline-banner';

import './timeline-banner.module.css';

type Props = {event: EventForTimeline};

const USER_DRIVEN_EVENTS = [
    'published_time',
    'sent_time',
    'revoked_time',
    'accepted_time',
    'archived_time',
];
const ALL_EVENTS = [
    'published_time',
    'sent_time',
    'revoked_time',
    'accepted_time',
    'archived_time',
];

export function TimelineBannerInvoice(props: Props) {
    const {event} = props;
    const {payload, changes} = event;

    const getAction = (
        attribute: string,
        invoiceNumber: string,
        value: string,
        targetEntities: React.Element<any>,
        invoiceModalUrl: string
    ) => {
        const invoiceNumberBold = getQuoteLinkText(invoiceModalUrl, invoiceNumber);

        switch (attribute) {
            case 'published_time':
                return (
                    <span>
                        marked invoice {invoiceNumberBold} as ready for <b>{targetEntities}</b>
                    </span>
                );
            case 'sent_time':
                return (
                    <span>
                        sent invoice {invoiceNumberBold} to <b>{targetEntities}</b>
                    </span>
                );
            case 'revoked_time':
                return (
                    <span>
                        voided invoice {invoiceNumberBold} valued at&nbsp;
                        {value}
                    </span>
                );
            case 'accepted_time':
                return (
                    <span>
                        marked invoice {invoiceNumberBold} generated for <b>{targetEntities}</b> as
                        paid
                    </span>
                );
            case 'archived_time':
                return (
                    <span>
                        archived invoice {invoiceNumberBold} for <b>{targetEntities}</b>
                    </span>
                );
            default:
                return <></>;
        }
    };

    /**
     * We need to include this check, even though we are only passing an event
     * with a payload.__typename of 'Invoice' because Apollo doesn't generate a specific
     * enough type when using a union type (like the TimelineEventPayload).
     * See this issue for more detail:
     * https://github.com/apollographql/apollo-tooling/issues/1223
     */
    if (payload.__typename === 'Invoice') {
        const change = changes.find((c) => ALL_EVENTS.includes(c.attribute));

        const targetEntities = getTargetEntities(payload.lead);

        const targetEntitiesDisplay = makeTargetEntitiesDisplay(
            targetEntities,
            payload.lead.name,
            payload.lead.htmlUrl
        );

        const invoiceModalUrl = payload.modalUrl;
        const displayNumber = payload.displayNumber;
        const attribute = change ? change.attribute : '';
        const totalValue = payload.total.formattedWithCents;

        // If this is a recipient driven event, we want to inject our own actor
        // so we can display both company and contacts
        const actor = USER_DRIVEN_EVENTS.includes(attribute) ? payload.creatorUser : null;
        const omitActor = actor === null;

        const action = getAction(
            attribute,
            displayNumber,
            totalValue,
            targetEntitiesDisplay,
            invoiceModalUrl
        );

        return (
            <TimelineBanner
                id={event.id}
                iconVariant='invoice'
                timestamp={event.changeTime}
                actor={actor}
                action={action}
                omitActor={omitActor}
            />
        );
    }

    return <></>;
}
